/**
 * 🛟 Inloggen als zaak (prototype startSupport, renderSupBar; blok K): Tafo-beheer opens the shop's till in
 * a window of its own, with a till token that works 2 hours. The token travels in the address fragment
 * (never sent to a server), is kept for this window only (sessionStorage) and never replaces the token of
 * a till that is paired on this computer.
 */
const KEY = "tafo.support";
const ENDED = "tafo.support.ended";
export const SUPPORT_ENDED_EVENT = "tafo-support-ended";

export interface SupportWindow {
  token: string;
  /** The shop's name, for the yellow bar. */
  shop: string;
  /** When the session started (ISO). */
  since: string;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export const supportWindow = {
  read(): SupportWindow | null {
    try {
      const value = storage()?.getItem(KEY);
      if (!value) return null;
      const parsed = JSON.parse(value) as Partial<SupportWindow>;
      return typeof parsed.token === "string" && typeof parsed.shop === "string" && typeof parsed.since === "string"
        ? { token: parsed.token, shop: parsed.shop, since: parsed.since }
        : null;
    } catch {
      return null;
    }
  },
  start(session: SupportWindow): void {
    storage()?.setItem(KEY, JSON.stringify(session));
    storage()?.removeItem(ENDED);
  },
  /** Stopped, or the till token ran out: this window shows that it ended (never the pairing screen). */
  end(): void {
    storage()?.removeItem(KEY);
    storage()?.setItem(ENDED, "1");
    window.dispatchEvent(new Event(SUPPORT_ENDED_EVENT));
  },
  ended(): boolean {
    return storage()?.getItem(ENDED) === "1";
  },
};

/**
 * /support#t=…&zaak=…&sinds=… from Tafo-beheer: takes the session and cleans the address (the token does
 * not stay in the history). Returns whether this window is a support window.
 */
export function takeSupportFromAddress(location: Location = window.location): boolean {
  if (location.pathname !== "/support") return supportWindow.read() !== null || supportWindow.ended();
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ""));
  const token = fragment.get("t");
  if (token) {
    supportWindow.start({
      token,
      shop: fragment.get("zaak") ?? "",
      since: fragment.get("sinds") ?? new Date().toISOString(),
    });
  }
  window.history.replaceState(null, "", "/");
  return supportWindow.read() !== null || supportWindow.ended();
}
